/**
 * The office lodge for all five registers as one pure function (13-19): FIELDSET §3.2, with the §1.3 authority
 * floor held by construction.
 *
 * `buildLodge(register, values, actor, now, opts)` → `{ ok: true, fields, siblings, warnings }` or
 * `{ ok: false, refusals, warnings }`. It is the first write after 1 Dec for every record a Care Manager raises.
 *
 * Driven by 13-17's field set (`config/fieldset/`): the lodge columns, their kinds and caps, their `lodge`
 * `RequiredRule`s, `CLIENT_STREAMS`, the live choices and the floor. Values go through 13-18's `lib/fieldValues`
 * (dates, `cleanText`, `checkChoice`, `checkUpn`); nothing here retypes a choice, a cap or a date rule. The only
 * register-specific code is what FIELDSET writes in words and a `RequiredRule` cannot say (D-13-19-2):
 * - which conditional columns are refused when their condition does not hold (FieldLink's "otherwise refused");
 * - the anonymous contact columns (§1.6: "when `Anonymous` = Yes, the contact columns stay blank");
 * - the unlisted client (D-13-L-7), a form flag rather than a column;
 * - the identity column (D-13-L-8) and the `ReportedOn` pre-fill (ISS-200 option (b)).
 *
 * The floor, by construction: `fields` is assembled only from `lodgeColumns(register)` plus the two lodge constants,
 * and every key is checked with `lodgeMayWrite` before it is returned. A floor column in `values` is refused
 * `floor-column`, never dropped silently, so the form learns it sent one (D-13-19-3).
 *
 * Pure: no Graph, no React, no `Date.now()`. `now` and `opts.auditId` come from the caller. `ParentRef` is left
 * unset: the service composes `<PREFIX>-<new SP Id>` after the create (ISS-008), and the create-then-audit order
 * stays in `services/lodgeService.ts` (13-23 / 13-24 wire this in).
 */

import { AUTHORITY_FLOOR, LODGE_CONSTANTS, lodgeMayWrite } from '../config/fieldset/floor';
import { liveChoice } from '../config/fieldset/live';
import {
  CLIENT_STREAMS,
  RECORD_REGISTERS,
  choicesOf,
  fieldOf,
  lodgeColumns,
  type FieldSpec,
} from '../config/fieldset/registerFields';
import type { LodgeResult, RecordPatch, RecordRegister, Refusal, RequiredRule, SiblingDraft } from '../types/records';
import {
  checkChoice,
  checkUpn,
  classifyInstant,
  cleanText,
  dayExisting,
  melbourneDayOf,
  melbourneWallToInstant,
  notInFuture,
  toInstant,
} from './fieldValues';
import { actionTag } from './outbox';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The signed-in lodger (MSAL). `siteUserId` = the User Information List id, when it resolved (D-13-00-4). */
export interface LodgeActor {
  upn: string;
  siteUserId?: string;
}

/** What the form says about the submit, beside the column values (D-13-19-1). */
export interface LodgeOptions {
  /** The `[a:<id>]` tag for the sibling `Note` (D-13-07-3, D-13-L-5). From the caller, so tests are deterministic. */
  auditId: string;
  /** The CM ticked "Not on the client lists" (D-13-L-7): `RecipientClientId` stays blank and the Note says so. */
  clientUnlisted?: boolean;
  /** The form showed `ReportedOn` pre-filled with the submit instant and the user left it (FIELDSET §3.2.1 row 9). */
  reportedOnDefaulted?: boolean;
}

/** A Melbourne wall-clock entry: a `YYYY-MM-DD` day, and an `HH:mm` (24 h) time when known. */
export interface WallTime {
  day: string;
  time?: string;
}

/**
 * The form's values, keyed by SharePoint internal name. `unknown` on purpose: the builder checks every value.
 * Text and choice columns take a string; day columns a `YYYY-MM-DD` string or a `WallTime`; `ReportedToAt` a
 * `WallTime` with both parts.
 */
export type LodgeValues = Readonly<Record<string, unknown>>;

/** A lodge build. `warnings` (e.g. `contact-in-narrative`) never block it and come back either way. */
export type LodgeBuild =
  | ({ ok: true; warnings: Refusal[] } & LodgeResult)
  | { ok: false; refusals: Refusal[]; warnings: Refusal[] };

// ---------------------------------------------------------------------------
// What FIELDSET writes in words (D-13-19-2)
// ---------------------------------------------------------------------------

/** The lodger's UPN, from MSAL, never a form field (D-13-L-8, FIELDSET §4.1 LOD). A value in `values` is ignored. */
const IDENTITY_COLUMN: Partial<Record<RecordRegister, string>> = { incidents: 'ReportedByEmail', ci: 'RaisedByUPN' };

/**
 * Conditional columns that must be left blank when their `lodge` rule's condition does not hold (FieldLink's
 * `harmed`, `toldSomeone` and `workerInjured` rules, "then required, otherwise refused"; 12.1-14.1).
 */
const ONLY_WHEN: Partial<Record<RecordRegister, readonly string[]>> = {
  // 13.2 (D-13.2-P-2): the injured worker by name; the office lodge never writes `InjuredWorkerUPN`.
  incidents: ['HarmDescription', 'ReportedToAt', 'InjuredWorkerName', 'InjuredWorkerOccupation'],
};

/** FIELDSET §1.6 / §3.2.2–§3.2.3: blank when `Anonymous` = Yes. */
const ANONYMOUS_CONTACT: Partial<Record<RecordRegister, readonly string[]>> = {
  complaints: ['ComplainantName', 'ComplainantPhone', 'ComplainantEmail'],
  feedback: ['ProviderName', 'ProviderPhone', 'ProviderEmail'],
};

/** The `Anonymous` value that blanks the contacts, checked live per register. */
const ANONYMOUS_YES: Partial<Record<RecordRegister, string>> = {
  complaints: liveChoice('complaints', 'Anonymous', 'Yes')[0],
  feedback: liveChoice('feedback', 'Anonymous', 'Yes')[0],
};

/** The client columns (D-13-L-7). */
const RECIPIENT_NAME = 'RecipientName';
const RECIPIENT_CLIENT_ID = 'RecipientClientId';

/** The pre-fillable day (FIELDSET §3.2.1 row 9) and the one it may not precede (FieldLink's check). */
const REPORTED_ON = 'ReportedOn';
const OCCURRED_ON = 'OccurredOn';

/**
 * FIELDSET §1.6's contact columns: contact details belong here, so they are never scanned for them. Exported for the
 * record page (13-25), which shows them only in their own section.
 */
export const CONTACT_COLUMNS: ReadonlySet<string> = new Set([
  'ReporterPhone', 'PersonsContactDetails', 'WitnessContactDetails',
  ...Object.values(ANONYMOUS_CONTACT).flat(),
]);

/** Every floor column on any register: never a lodge write, whichever register it is sent to (D-13-19-3). */
const ANY_FLOOR: ReadonlySet<string> = new Set(RECORD_REGISTERS.flatMap((r) => [...AUTHORITY_FLOOR[r]]));

const UNLISTED_NOTE = 'Client not on the client lists.';

/** The builder's word-rules for one register, read-only, so the lodge form reveals and clears exactly these (13-24). */
export interface LodgeConditions {
  /** The column filled from the signed-in UPN, never a form field (D-13-L-8). */
  identity?: string;
  /** Conditional columns refused when their `lodge` rule's condition does not hold. */
  onlyWhen: readonly string[];
  /** The contact columns that stay blank when `Anonymous` = `anonymousYes` (§1.6). */
  anonymousContact: readonly string[];
  anonymousYes?: string;
}

export function lodgeConditions(register: RecordRegister): LodgeConditions {
  const identity = IDENTITY_COLUMN[register];
  const anonymousYes = ANONYMOUS_YES[register];
  return {
    ...(identity ? { identity } : {}),
    onlyWhen: ONLY_WHEN[register] ?? [],
    anonymousContact: ANONYMOUS_CONTACT[register] ?? [],
    ...(anonymousYes ? { anonymousYes } : {}),
  };
}

// ---------------------------------------------------------------------------
// Contact details in a narrative (FIELDSET §1.6): a warning, not a refusal (D-13-19-4)
// ---------------------------------------------------------------------------

const PHONE_PATTERNS: readonly RegExp[] = [
  /\+61[\s-]?\(?0?\d\)?(?:[\s-]?\d){8}(?!\d)/, // +61 4xx xxx xxx, +61 (0)3 xxxx xxxx
  /\(0\d\)[\s-]?\d(?:[\s-]?\d){7}(?!\d)/, // (03) 9xxx xxxx
  /(?<![\d/.:-])0\d(?:[\s-]?\d){8}(?![\d/:-])/, // 04xx xxx xxx, 03 9xxx xxxx (a full stop may end the sentence)
  /(?<![\d/.:-])1[38]00(?:[\s-]?\d){6}(?![\d/:-])/, // 1300 / 1800 numbers
];
const EMAIL_PATTERN = /[^\s@<>()[\]]+@[^\s@<>()[\]]+\.[a-z]{2,}/i;

/** Does `text` look like it holds a phone number or an email address (FIELDSET §1.6)? Shared with `recordVerbs`. */
export function looksLikeContact(text: string): boolean {
  return EMAIL_PATTERN.test(text) || PHONE_PATTERNS.some((re) => re.test(text));
}

/**
 * The §1.6 warning for one narrative value (a text area that is not a contact column), or `undefined`. Shared with
 * `recordVerbs`' `complete` (13.2-03), so a report completed later warns exactly as one lodged complete.
 */
export function contactWarning(register: RecordRegister, column: string, value: string): Refusal | undefined {
  const spec = fieldOf(register, column);
  if (!spec || spec.kind !== 'textarea' || CONTACT_COLUMNS.has(column) || !looksLikeContact(value)) return undefined;
  return {
    code: 'contact-in-narrative',
    column,
    message: `${spec.label} looks like it contains a phone number or email. Contact details go in their own fields.`,
  };
}

/**
 * FieldLink's check (D-12.1-14.1-1): is `reported` before `occurred`? By Melbourne day, and by the time when both
 * carry one. Shared with `recordVerbs`' `complete` (13.2-03).
 */
export function reportedBeforeOccurred(occurred: string, reported: string): boolean {
  const dayO = melbourneDayOf(occurred);
  const dayR = melbourneDayOf(reported);
  if (!dayO || !dayR) return false;
  const timed = classifyInstant(occurred) === 'time-of-day' && classifyInstant(reported) === 'time-of-day';
  return dayR < dayO || (dayR === dayO && timed && Date.parse(reported) < Date.parse(occurred));
}

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/** A lodge value after `cleanLodgeValue`. */
export type LodgeCleaned =
  | { kind: 'blank' }
  | { kind: 'value'; value: string; warning?: Refusal }
  | { kind: 'refused'; code: string; message: string };

type Cleaned = LodgeCleaned;

const blank: Cleaned = { kind: 'blank' };
const refusedValue = (code: string, message: string): Cleaned => ({ kind: 'refused', code, message });

function isWallTime(value: unknown): value is WallTime {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'day' in value;
}

function isBlankRaw(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/** A day column's payload (§1.4): a day → UTC midnight; a day + time (when the column takes one) → an instant. */
function cleanDay(spec: FieldSpec, raw: unknown, now: Date): Cleaned {
  let day: unknown;
  let time: unknown;
  if (typeof raw === 'string') day = raw;
  else if (isWallTime(raw)) {
    day = raw.day;
    time = raw.time;
  } else return refusedValue('invalid-date', `${spec.label}: enter a date.`);

  if (isBlankRaw(day) && isBlankRaw(time)) return blank;
  if (typeof day !== 'string') return refusedValue('invalid-day', `${spec.label}: enter a date as YYYY-MM-DD.`);

  const timed = !isBlankRaw(time);
  if (spec.kind === 'instant' && !timed) {
    return refusedValue('invalid-date', `${spec.label}: enter a date and a time.`);
  }
  if (spec.kind === 'day-existing' && timed && !spec.timeWhenKnown) {
    return refusedValue('invalid-date', `${spec.label} takes a date, not a time.`);
  }

  let value: string;
  let warning: Refusal | undefined;
  if (timed) {
    const r = melbourneWallToInstant(day, typeof time === 'string' ? time : '');
    if (!r.ok) {
      const message =
        r.code === 'nonexistent-time'
          ? `${spec.label}: that time didn't exist that night (clocks went forward). Check the time.`
          : r.code === 'invalid-time'
            ? `${spec.label}: enter the time as HH:MM (24-hour).`
            : `${spec.label}: enter a real date as YYYY-MM-DD.`;
      return refusedValue(r.code, message);
    }
    value = r.value;
    if (r.ambiguous) {
      warning = {
        code: 'ambiguous-time',
        column: spec.name,
        message: `${spec.label}: ${String(time)} happened twice that night (clocks went back); we've recorded the first, AEDT.`,
      };
    }
  } else {
    const r = dayExisting(day);
    if (!r.ok) return refusedValue(r.code, `${spec.label}: enter a real date as YYYY-MM-DD.`);
    value = r.value;
  }

  const future = notInFuture(value, now);
  if (!future.ok) return refusedValue(future.code, `${spec.label} can't be in the future.`);
  return { kind: 'value', value, ...(warning ? { warning } : {}) };
}

/**
 * One lodge column's value, cleaned and checked by its kind (days, wall time to an instant, caps, `notInFuture`).
 * Exported for `recordVerbs`' `complete` (13.2-03), so a report completed later and one lodged complete store the same
 * value for the same input, and refuse a bad one in the same words.
 */
export function cleanLodgeValue(register: RecordRegister, spec: FieldSpec, raw: unknown, now: Date): Cleaned {
  switch (spec.kind) {
    case 'text':
    case 'textarea':
    case 'position': {
      if (raw === null || raw === undefined) return blank;
      if (spec.cap === undefined) throw new Error(`lodgeBuilder: ${register}.${spec.name} has no cap`);
      const r = cleanText(raw as string, spec.cap);
      if (!r.ok) {
        return r.code === 'too-long'
          ? refusedValue('too-long', `${spec.label} is ${r.length} characters; the limit is ${r.cap}.`)
          : refusedValue('not-text', `${spec.label} must be text.`);
      }
      return r.value === '' ? blank : { kind: 'value', value: r.value };
    }
    case 'choice': {
      if (raw === null || raw === undefined || raw === '') return blank;
      const r = checkChoice(raw as string, choicesOf(register, spec.name));
      return r.ok ? { kind: 'value', value: r.value } : refusedValue(r.code, `${spec.label}: choose one of the listed options.`);
    }
    case 'upn': {
      if (isBlankRaw(raw)) return blank;
      const r = checkUpn(raw as string);
      if (r.ok) return { kind: 'value', value: r.value };
      return r.code === 'guest-upn'
        ? refusedValue(r.code, `${spec.label}: a guest account can't be recorded here; use their Harmony account.`)
        : refusedValue(r.code, `${spec.label}: enter one email address.`);
    }
    case 'day-existing':
    case 'instant':
      if (raw === null || raw === undefined) return blank;
      return cleanDay(spec, raw, now);
    case 'attachments':
      if (raw === null || raw === undefined || (Array.isArray(raw) && raw.length === 0)) return blank;
      return refusedValue('not-writable', `${spec.label}: add attachments on the item's SharePoint form after lodging.`);
    default:
      // A §3.2 column of a kind the lodge doesn't write (a config change): fail loudly, never write blind.
      throw new Error(`lodgeBuilder: ${register}.${spec.name} has kind ${spec.kind}, which the lodge does not write`);
  }
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

type Holds = true | false | 'unknown';

/** Does a lodge rule's condition hold? `unknown` when the column it reads was itself refused. */
function holds(rule: RequiredRule | undefined, clean: ReadonlyMap<string, string>, bad: ReadonlySet<string>): Holds {
  if (rule === 'always') return true;
  if (rule === undefined || rule === 'optional') return false;
  if (bad.has(rule.when)) return 'unknown';
  const v = clean.get(rule.when) ?? '';
  return 'in' in rule ? rule.in.includes(v) : !rule.notIn.includes(v);
}

/** A rule's condition in words, for a message ("Was anyone harmed? is Harm or Potential harm"). Shared with `recordVerbs`. */
export function describeRule(register: RecordRegister, rule: RequiredRule): string {
  if (typeof rule !== 'object') return '';
  const label = fieldOf(register, rule.when)?.label ?? rule.when;
  if ('in' in rule) {
    if (rule.in === CLIENT_STREAMS) return `${label} is a client stream`;
    return `${label} is ${rule.in.join(' or ')}`;
  }
  if (rule.notIn.length === 1 && rule.notIn[0] === '') return `${label} is given`;
  return `${label} is not ${rule.notIn.join(' or ')}`;
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

/**
 * Build the office lodge for `register`: the create payload and its one `StateChange` sibling draft, or every
 * refusal in one pass. See the module comment for the rules and their sources.
 */
export function buildLodge(
  register: RecordRegister,
  values: LodgeValues,
  actor: LodgeActor,
  now: Date,
  opts: LodgeOptions,
): LodgeBuild {
  if (!opts || typeof opts.auditId !== 'string' || opts.auditId.trim() === '') {
    throw new Error('buildLodge: opts.auditId is required (the [a:<id>] tag)');
  }
  const refusals: Refusal[] = [];
  const warnings: Refusal[] = [];
  const refuse = (code: string, column: string | undefined, message: string) =>
    refusals.push(column === undefined ? { code, message } : { code, column, message });

  const columns = lodgeColumns(register);
  const lodgeSet = new Set(columns);
  const identity = IDENTITY_COLUMN[register];
  const clean = new Map<string, string>();
  const bad = new Set<string>();

  // --- The actor (D-13-L-8) ------------------------------------------------------------------------------------
  const actorUpn = checkUpn(actor?.upn as string);
  if (!actorUpn.ok) refuse('invalid-actor', undefined, 'The signed-in account has no usable email address.');

  // --- Pass 1: every key in values ------------------------------------------------------------------------------
  for (const [key, raw] of Object.entries(values ?? {})) {
    if (raw === undefined) continue;
    if (key === identity) continue; // never a form field (D-13-L-8): ignored, the actor's UPN is written
    if (!lodgeSet.has(key)) {
      if (ANY_FLOOR.has(key)) {
        refuse('floor-column', key, `${key} isn't set when lodging; it is recorded later, by the verb that owns it.`);
      } else {
        refuse('not-a-lodge-column', key, `${key} isn't a field on this lodge form.`);
      }
      continue;
    }
    const spec = fieldOf(register, key);
    if (!spec) throw new Error(`lodgeBuilder: ${register} lodge column ${key} has no field spec`);
    const r = cleanLodgeValue(register, spec, raw, now);
    if (r.kind === 'blank') continue;
    if (r.kind === 'refused') {
      refuse(r.code, key, r.message);
      bad.add(key);
      continue;
    }
    clean.set(key, r.value);
    if (r.warning) warnings.push(r.warning);
  }

  // --- System values: the identity column and the ReportedOn pre-fill ------------------------------------------
  if (identity && actorUpn.ok) clean.set(identity, actorUpn.value);
  if (lodgeSet.has(REPORTED_ON) && !clean.has(REPORTED_ON) && !bad.has(REPORTED_ON) && opts.reportedOnDefaulted) {
    clean.set(REPORTED_ON, toInstant(now));
  }

  // --- Pass 2: required, only-when, anonymous, unlisted ---------------------------------------------------------
  const unlisted = opts.clientUnlisted === true;
  const anonymousYes = ANONYMOUS_YES[register];
  const isAnonymous = anonymousYes !== undefined && clean.get('Anonymous') === anonymousYes;

  for (const column of columns) {
    const spec = fieldOf(register, column) as FieldSpec;
    const rule = spec.required.lodge;
    if (bad.has(column)) continue; // already refused once

    if (!clean.has(column)) {
      if (column === identity) continue; // an unusable actor is refused once, above
      if (column === RECIPIENT_CLIENT_ID && unlisted) continue; // D-13-L-7
      if (holds(rule, clean, bad) === true) {
        const when = typeof rule === 'object' ? ` when ${describeRule(register, rule)}` : '';
        refuse('required', column, `${spec.label} is required${when}.`);
      }
      continue;
    }

    if (isAnonymous && ANONYMOUS_CONTACT[register]?.includes(column)) {
      refuse('anonymous-contact', column, `${spec.label} stays blank on an anonymous record. Remove it, or set Anonymous? to No.`);
      continue;
    }
    if (ONLY_WHEN[register]?.includes(column) && rule !== undefined && holds(rule, clean, bad) === false) {
      refuse('not-applicable', column, `Leave ${spec.label} blank unless ${describeRule(register, rule)}.`);
      continue;
    }
    if (column === RECIPIENT_CLIENT_ID) {
      if (unlisted) {
        refuse('not-applicable', column, `${spec.label}: the client is marked as not on the client lists, so leave it blank.`);
      } else if (!clean.has(RECIPIENT_NAME) && !bad.has(RECIPIENT_NAME)) {
        refuse('not-applicable', column, `${spec.label} needs the client's name too.`);
      }
    }
  }

  // --- Pass 3: ReportedOn may not precede OccurredOn (FieldLink's check, D-12.1-14.1-1) ------------------------
  const occurred = clean.get(OCCURRED_ON);
  const reported = clean.get(REPORTED_ON);
  if (lodgeSet.has(REPORTED_ON) && occurred && reported && reportedBeforeOccurred(occurred, reported)) {
    const label = fieldOf(register, REPORTED_ON)?.label ?? REPORTED_ON;
    refuse('before-occurred', REPORTED_ON, `${label} can't be before the incident happened.`);
  }

  // --- Warnings: contact details in a narrative column (§1.6) --------------------------------------------------
  for (const column of columns) {
    const value = clean.get(column);
    const warning = value === undefined ? undefined : contactWarning(register, column, value);
    if (warning) warnings.push(warning);
  }

  if (refusals.length > 0) return { ok: false, refusals, warnings };

  // --- Assemble: lodge columns with a value, then the two constants (§1.3) -------------------------------------
  const fields: RecordPatch = {};
  for (const column of columns) {
    const value = clean.get(column);
    if (value !== undefined) fields[column] = value;
  }
  const constants = LODGE_CONSTANTS[register];
  fields.Status = constants.Status;
  fields.ClosureStatus = constants.ClosureStatus;
  for (const [k, v] of Object.entries(fields)) {
    if (!lodgeMayWrite(register, k, v) || (!lodgeSet.has(k) && k !== 'Status' && k !== 'ClosureStatus')) {
      throw new Error(`lodgeBuilder: ${register}.${k} reached a lodge payload (authority floor)`);
    }
  }

  // --- The StateChange sibling draft (§1.7, D-13-L-8) ----------------------------------------------------------
  const upn = actorUpn.ok ? actorUpn.value : '';
  const unlistedNote = unlisted && fields[RECIPIENT_NAME] !== undefined && fields[RECIPIENT_CLIENT_ID] === undefined;
  const sibling: SiblingDraft = {
    Title: 'lodge: Status',
    EntryType: 'StateChange',
    FieldChanged: 'Status',
    OldValue: '',
    NewValue: constants.Status,
    Note: `Lodged by ${upn}. ${actionTag(opts.auditId)}${unlistedNote ? ` ${UNLISTED_NOTE}` : ''}`,
    EntryTimestamp: toInstant(now),
    ActorUPN: upn,
    ...(actor?.siteUserId ? { ActorLookupId: actor.siteUserId } : {}),
    auditId: opts.auditId,
  };

  return { ok: true, fields, siblings: [sibling], warnings };
}
